// Fitness analytics — the MODULE-SPECIFIC half of the pilot: a declarative
// config + a rows→samples extractor. Everything else (aggregation, trend,
// streak, projection, charts, cards) is the shared engine/dashboard. A second
// module (Sleep, Nutrition…) becomes a dashboard by writing a file exactly like
// this one — no new engine or UI code.
//
// GUARDRAIL (Fitness = no prescription): this file only declares WHICH numbers
// to plot. It carries no advice; the dashboard renders them descriptively.

import type { Sample } from "./engine";
import type { DashboardConfig } from "./dashboard";
import {
  getGymVolumeKg,
  getSessionKind,
  type WorkoutSessionRow,
} from "@/lib/fitness/sessionKind";

export const FITNESS_DASHBOARD: DashboardConfig = {
  moduleKey: "fitness",
  titleKey: "fitness.analytics.title",
  defaultGranularity: "week",
  metrics: [
    {
      key: "frequency",
      labelKey: "fitness.analytics.metric.frequency",
      reducer: "count",
      color: "hsl(187 72% 55%)",
      chart: "bar",
      projectable: true,
      streak: true, // workout-day streak drives the streak card
    },
    {
      key: "duration",
      labelKey: "fitness.analytics.metric.duration",
      reducer: "sum",
      color: "hsl(140 60% 55%)",
      unitKey: "fitness.analytics.unit.min",
      chart: "line",
      projectable: true,
    },
    {
      key: "volume",
      labelKey: "fitness.analytics.metric.volume",
      reducer: "sum",
      color: "hsl(265 60% 65%)",
      unitKey: "fitness.analytics.unit.kg",
      chart: "line",
      projectable: true,
    },
    {
      key: "calories",
      labelKey: "fitness.analytics.metric.calories",
      reducer: "sum",
      color: "hsl(30 90% 55%)",
      unitKey: "fitness.analytics.unit.kcal",
      chart: "line",
      projectable: false, // sparse (Strava-only) — let it show trend, not a forecast
    },
  ],
};

/**
 * Map completed workout_sessions rows to per-metric Samples. Reuses the
 * canonical volume/kind helpers (sessionKind.ts) so gym volume and the
 * gym-vs-activity split stay consistent with the rest of Fitness.
 */
export function extractFitnessSamples(rows: WorkoutSessionRow[]): Record<string, Sample[]> {
  const frequency: Sample[] = [];
  const duration: Sample[] = [];
  const volume: Sample[] = [];
  const calories: Sample[] = [];

  for (const r of rows) {
    if (!r.completed_at) continue;
    const t = new Date(r.completed_at).getTime();
    if (!Number.isFinite(t)) continue;
    const category = getSessionKind(r); // "gym" | "activity"

    frequency.push({ t, v: 1, category });
    if (typeof r.duration_minutes === "number" && r.duration_minutes > 0) {
      duration.push({ t, v: r.duration_minutes, category });
    }
    const vol = getGymVolumeKg(r);
    if (vol > 0) volume.push({ t, v: vol, category });
    if (typeof r.calories_burned === "number" && r.calories_burned > 0) {
      calories.push({ t, v: r.calories_burned, category });
    }
  }

  return { frequency, duration, volume, calories };
}
